import type { FrameApplyContext } from "./companion-frame-reducer";
import { payloadString, resolveWorkToastQuestion } from "./work-toast-item";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import {
  useWorkToastStore,
  type WorkToastItem,
} from "@/stores/work-toast-store";
import type { WsMessage } from "@/api/websocket";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

type Context = Pick<FrameApplyContext, "workToastStore">;
const RANK = {
  pending: 0,
  unavailable: 1,
  superseded: 2,
  delivered: 3,
} as const;
function isDeliveryStatus(value: unknown): value is keyof typeof RANK {
  return (
    value === "pending" ||
    value === "unavailable" ||
    value === "superseded" ||
    value === "delivered"
  );
}
// Rendering retention must not erase delivery knowledge: a slow REST response
// can arrive after its toast was pruned. These token-free receipts live only
// for the current authenticated webview lifetime.
const receipts = new Map<string, keyof typeof RANK>();
function rememberReceipt(id: string, status: keyof typeof RANK) {
  const known = receipts.get(id);
  const latest = known && RANK[known] > RANK[status] ? known : status;
  receipts.set(id, latest);
  return latest;
}
export function clearWorkToastQuestionReceipts() {
  receipts.clear();
}
subscribeDesktopChatAuthScope(clearWorkToastQuestionReceipts);
export function isWorkToastQuestionAnswered(id: string) {
  return receipts.has(id);
}

/** Reject before owner fanout: a new mirror may have missed the receipt and
 *  its pruned toast, so it cannot independently reject this older event. */
export function isStaleWorkToastQuestionFrame(message: WsMessage): boolean {
  const id = payloadString(message.data, "awaiting_input_id");
  if (!id) return false;
  const known = receipts.get(id);
  if (known === undefined) return false;
  if (
    message.type === "cursor.question" ||
    message.type === "awaiting_input.requested"
  )
    return true;
  const status =
    message.type === "question.answer_delivery"
      ? message.data.status
      : message.type === "awaiting_input.resolved"
        ? message.data.answer_delivery_status
        : null;
  return isDeliveryStatus(status) && RANK[known] > RANK[status];
}

// Mirrors hydrate snapshots and REST mutations without replaying the frame
// that created a receipt. Remember those receipts before any stale frame or
// pruning can remove their visible evidence. Unchanged rows are not re-read
// after an auth reset while the old store is being cleared.
useWorkToastStore.subscribe((state, previous) => {
  if (state.items === previous.items) return;
  for (const item of Object.values(state.items)) {
    const receipt = item.answerDelivery;
    if (receipt && receipt !== previous.items[item.key]?.answerDelivery) {
      rememberReceipt(receipt.awaitingInputId, receipt.status);
    }
  }
});

/** A mirrored `items` patch replaces the receiving window's whole map, so a
 *  stale copy of an answer row would roll its delivery back. Per key, keep
 *  the copy whose receipt for the same answer ranks higher; every other row,
 *  removal and dismissal follows the patch. */
export function keepHigherDeliveryReceipts(
  current: Record<string, WorkToastItem>,
  incoming: Record<string, WorkToastItem>,
): Record<string, WorkToastItem> {
  let merged: Record<string, WorkToastItem> | null = null;
  for (const [key, offered] of Object.entries(incoming)) {
    const held = current[key];
    const heldReceipt = held?.answerDelivery;
    const offeredReceipt = offered.answerDelivery;
    if (!heldReceipt || !offeredReceipt) continue;
    if (heldReceipt.awaitingInputId !== offeredReceipt.awaitingInputId)
      continue;
    if (RANK[heldReceipt.status] <= RANK[offeredReceipt.status]) continue;
    merged ??= { ...incoming };
    merged[key] = held;
  }
  return merged ?? incoming;
}

/** Keep saved-but-undelivered answers visible without changing run/task status. */
export function applyWorkToastQuestionDelivery(
  data: Record<string, unknown>,
  ctx: Context,
  now: number,
) {
  const questionId = payloadString(data, "awaiting_input_id");
  const executionId = payloadString(data, "execution_id");
  const status = data.status;
  if (!questionId || !executionId || !isDeliveryStatus(status)) return;
  const key = `answer:${questionId}`;
  const store = ctx.workToastStore.getState();
  const previous = store.items[key];
  // REST reply responses and channel events can arrive in either order.
  if (previous?.answerDelivery)
    rememberReceipt(questionId, previous.answerDelivery.status);
  if (rememberReceipt(questionId, status) !== status) return;
  const origin = store.items[executionId];
  const settled = status === "delivered" || status === "superseded";
  resolveWorkToastQuestion(ctx, questionId, now);
  store.upsertItem({
    key,
    source: "agent",
    tracked: false,
    state: settled ? "done" : "blocked",
    stateBeforeBlocked: null,
    agentName: origin?.agentName ?? previous?.agentName ?? "Denker",
    projectId:
      payloadString(data, "project_id") ??
      origin?.projectId ??
      previous?.projectId ??
      null,
    conversationId:
      payloadString(data, "conversation_id") ??
      origin?.conversationId ??
      previous?.conversationId ??
      null,
    taskId:
      payloadString(data, "task_id") ??
      origin?.taskId ??
      previous?.taskId ??
      null,
    nativeSubagentId:
      payloadString(data, "native_subagent_id") ??
      origin?.nativeSubagentId ??
      previous?.nativeSubagentId ??
      null,
    detail:
      status === "pending"
        ? translate(libChatMessages, "replySaved")
        : status === "unavailable"
          ? (payloadString(data, "error") ??
            translate(libChatMessages, "replyUnreachable"))
          : status === "superseded"
            ? translate(libChatMessages, "replyContinuedInChat")
            : translate(libChatMessages, "replyDelivered"),
    resultText: null,
    question: null,
    approval: null,
    answerDelivery: { awaitingInputId: questionId, executionId, status },
    stateChangedAt:
      previous?.answerDelivery?.status === status
        ? previous.stateChangedAt
        : now,
    // A delivered tombstone fences a slower REST response for the same answer.
    dismissed:
      settled ||
      (previous?.answerDelivery?.status === status && previous.dismissed) ||
      false,
  });
}
